/* eslint-disable import/no-mutable-exports */
import MockFeaturesFlag from '../jsons/features-flag.json';

export type RuntimeConfig = Record<string, string>;

type ConfigChangeCallback = (config: RuntimeConfig) => void;

declare global {
  interface Window {
    runtimeConfig?: RuntimeConfig;
  }
}

let runtimeConfig: RuntimeConfig | null = null;
const configSubscribers = new Set<ConfigChangeCallback>();

export let API_KEY_INTERCOM = import.meta.env.VITE_API_KEY_INTERCOM;
export let USEPILOT_KEY = import.meta.env.VITE_USEPILOT_KEY;
export let ACTIVATE_POLLING_PERMISSONS = import.meta.env
  .VITE_ACTIVATE_POLLING_PERMISSONS;
export let API_KEY = import.meta.env.VITE_API_KEY;
export let APPLICATION_VERSION = import.meta.env.VITE_APPLICATION_VERSION;
export let DASH0_AUTH_TOKEN = import.meta.env.VITE_DASH0_AUTH_TOKEN;
export let DASH0_ENDPOINT = import.meta.env.VITE_DASH0_ENDPOINT;
export let DASH0_ENVIRONMENT = import.meta.env.VITE_DASH0_ENVIRONMENT;
export let DASH0_SAMPLING_RATE = import.meta.env.VITE_DASH0_SAMPLING_RATE;
export let DASH0_SERVICE = import.meta.env.VITE_DASH0_SERVICE;
export let DATE_PIPELINE_ENGINE_METRICS_PRODUCER = import.meta.env
  .VITE_DATE_PIPELINE_ENGINE_METRICS_PRODUCER;
export let DAYS_TO_SHOW_OLD_API_KEY_TAG = import.meta.env
  .VITE_DAYS_TO_SHOW_OLD_API_KEY_TAG;
export let DYNATRACE_APP_ID = import.meta.env.VITE_DYNATRACE_APP_ID;
export let EXPOSE_CYTOSCAPE = import.meta.env.VITE_EXPOSE_CYTOSCAPE;
export let FEATUREFLAG_API_KEY = import.meta.env.VITE_FEATUREFLAG_API_KEY;
export let FEATUREFLAG_URL = import.meta.env.VITE_FEATUREFLAG_URL;
export let FULLSTORY_RELAY = import.meta.env.VITE_FULLSTORY_RELAY;
export let FUSION_VERSION = import.meta.env.VITE_FUSION_VERSION;
export let GRAPHQL_SERVER = import.meta.env.VITE_GRAPHQL_SERVER;
export let PIPELINE_INTERCOM_FALLBACK_APIKEY = import.meta.env
  .VITE_PIPELINE_INTERCOM_FALLBACK_APIKEY;
export let PIPELINE_INTERCOM_FALLBACK_ENDPOINT = import.meta.env
  .VITE_PIPELINE_INTERCOM_FALLBACK_ENDPOINT;
export let POLLING_PERMISSIONS_INTERVAL = import.meta.env
  .VITE_POLLING_PERMISSIONS_INTERVAL;
export let PORTAL_FULLSTORY_ORG_ID = import.meta.env
  .VITE_PORTAL_FULLSTORY_ORG_ID;
export let PORTAL_URL = import.meta.env.VITE_PORTAL_URL;
export let RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
export let SHOW_LINK_TO_PIPELINE_SUBSCRIPTION_ARTICLE = import.meta.env
  .VITE_SHOW_LINK_TO_PIPELINE_SUBSCRIPTION_ARTICLE;
export let SHOW_LINK_TO_RTU_ARTICLE = import.meta.env
  .VITE_SHOW_LINK_TO_RTU_ARTICLE;
export let SHOW_OLD_API_KEY_TAG = import.meta.env.VITE_SHOW_OLD_API_KEY_TAG;
export let URL = import.meta.env.VITE_URL;
export let HEADER_LOGO = import.meta.env.VITE_HEADER_LOGO;
export let IS_POWERED = import.meta.env.VITE_IS_POWERED;
export let BUILD_LOGO = import.meta.env.VITE_BUILD_LOGO;
export let SIDEBAR_LOGO = import.meta.env.VITE_SIDEBAR_LOGO;
export let version = import.meta.env.VITE_VERSION;

function getConfigValue(key: string, fallback: string): string {
  if (runtimeConfig && runtimeConfig[key] !== undefined) {
    return runtimeConfig[key];
  }
  if (
    typeof window !== 'undefined' &&
    window.runtimeConfig &&
    window.runtimeConfig[key] !== undefined
  ) {
    return window.runtimeConfig[key];
  }
  return fallback;
}

export function updateConfigValues() {
  IS_POWERED = getConfigValue('IS_POWERED', 'false');
  HEADER_LOGO = getConfigValue(
    'HEADER_LOGO',
    '/assets/images/new-logo-with-text.png'
  );
  BUILD_LOGO = getConfigValue(
    'BUILD_LOGO',
    '/assets/images/new-logo-without-text.png'
  );
  SIDEBAR_LOGO = getConfigValue(
    'SIDEBAR_LOGO',
    '/assets/images/new-logo-with-text.png'
  );
  APPLICATION_VERSION = getConfigValue('APPLICATION_VERSION', '');
  DASH0_AUTH_TOKEN = getConfigValue('DASH0_AUTH_TOKEN', '');
  DASH0_ENDPOINT = getConfigValue(
    'DASH0_ENDPOINT',
    'https://ingress.dash0.com'
  );
  DASH0_SERVICE = getConfigValue('DASH0_SERVICE', 'digibee-portal');
  DASH0_ENVIRONMENT = getConfigValue('DASH0_ENVIRONMENT', 'local');
  DASH0_SAMPLING_RATE = getConfigValue('DASH0_SAMPLING_RATE', '');
  const graphqlServer = getConfigValue(
    'GRAPHQL_SERVER',
    `//${window.location.hostname}:8000/graphql`
  );
  GRAPHQL_SERVER = graphqlServer.startsWith('http')
    ? graphqlServer
    : `//${window.location.hostname}:8000/graphql`;
  SHOW_LINK_TO_PIPELINE_SUBSCRIPTION_ARTICLE = getConfigValue(
    'SHOW_LINK_TO_PIPELINE_SUBSCRIPTION_ARTICLE',
    'false'
  );
  SHOW_LINK_TO_RTU_ARTICLE = getConfigValue(
    'SHOW_LINK_TO_RTU_ARTICLE',
    'false'
  );
  API_KEY = getConfigValue('API_KEY', '');
  API_KEY_INTERCOM = getConfigValue(
    'API_KEY_INTERCOM',
    import.meta.env.VITE_API_KEY_INTERCOM
  );
  URL = getConfigValue('URL', 'https://core.stg.digibee.dev');
  RECAPTCHA_SITE_KEY = getConfigValue(
    'RECAPTCHA_SITE_KEY',
    '6LdxSE4oAAAAAM-gGoYKtQkVyCb6gx8X1zO9_AGF'
  );
  EXPOSE_CYTOSCAPE = getConfigValue('EXPOSE_CYTOSCAPE', 'true');
  version = getConfigValue('VERSION', '3.1');
  DYNATRACE_APP_ID = getConfigValue('DYNATRACE_APP_ID', '');
  DATE_PIPELINE_ENGINE_METRICS_PRODUCER = getConfigValue(
    'DATE_PIPELINE_ENGINE_METRICS_PRODUCER',
    '1619094963000'
  );
  SHOW_OLD_API_KEY_TAG = getConfigValue('SHOW_OLD_API_KEY_TAG', 'true');
  DAYS_TO_SHOW_OLD_API_KEY_TAG = getConfigValue(
    'DAYS_TO_SHOW_OLD_API_KEY_TAG',
    '90'
  );
  ACTIVATE_POLLING_PERMISSONS = getConfigValue(
    'ACTIVATE_POLLING_PERMISSONS',
    'true'
  );
  USEPILOT_KEY = getConfigValue('USEPILOT_KEY', 'NX-ad6864bc');
  POLLING_PERMISSIONS_INTERVAL = getConfigValue(
    'POLLING_PERMISSIONS_INTERVAL',
    '60000'
  );
  PIPELINE_INTERCOM_FALLBACK_ENDPOINT = getConfigValue(
    'PIPELINE_INTERCOM_FALLBACK_ENDPOINT',
    'https://test.godigibee.io/pipeline/digibee/v1/intercom-offline'
  );
  PIPELINE_INTERCOM_FALLBACK_APIKEY = getConfigValue(
    'PIPELINE_INTERCOM_FALLBACK_APIKEY',
    'BJsazPnTK2RB6q3afWmEUsQloEhxUjhf'
  );
  PORTAL_URL = getConfigValue('PORTAL_URL', '//{realm}.localhost:3000');
  PORTAL_FULLSTORY_ORG_ID = getConfigValue('PORTAL_FULLSTORY_ORG_ID', '');
  FULLSTORY_RELAY = getConfigValue('FULLSTORY_RELAY', 'false');
  FEATUREFLAG_API_KEY = getConfigValue('FEATUREFLAG_API_KEY', 'localhost');
  FEATUREFLAG_URL = getConfigValue('FEATUREFLAG_URL', '//localhost:3011/api');
  FUSION_VERSION = getConfigValue('FUSION_VERSION', '1.47.1');
}

export function subscribeToConfigChanges(callback: ConfigChangeCallback) {
  configSubscribers.add(callback);
  if (runtimeConfig) {
    callback(runtimeConfig);
  }
  return () => configSubscribers.delete(callback);
}

export function loadRuntimeConfig() {
  return fetch('/runtime-config.json')
    .then(r => r.json())
    .then((cfg: RuntimeConfig) => {
      runtimeConfig = cfg;
      window.runtimeConfig = cfg;

      updateConfigValues();

      configSubscribers.forEach(callback => callback(cfg));

      return cfg;
    })
    .catch(error => {
      console.error('Error loading config:', error);
      throw error;
    });
}

loadRuntimeConfig()
  .then(() => {
    updateConfigValues();
  })
  .catch(() => {});

export const FEATUREFLAG_MOCK_FEATURES =
  process.env.NODE_ENV === 'production' || FEATUREFLAG_API_KEY !== 'localhost'
    ? null
    : MockFeaturesFlag;
